import { Button, Collapse, Form, Input, InputNumber, Popconfirm, Popover, Select, Space, Switch, Tag, TimePicker, Typography } from "antd";
import { DASHBOARD_REPORT_ACTION_KEY, FREQUENCY_DATA, FREQUENCY_DATA_KEY_TYPE, IAutomaticAction, IAutomaticActionType, IIntegrations, PLAYER_NOTIFY_ACTION_KEY } from "./consts";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import "./styles.css"
import { TrainingAPI } from "@api/TrainingAPI";
import { audioMessage } from "@components/Common/Notifications/audioMessage";
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { PresetConfigDisplay } from '@components/Settings/AutomaticActions/PresetConfigDisplay';
import { DateRangeField, DateRangeValue } from '@components/Settings/AutomaticActions/DateRangeField';
import { REPORT_TYPES } from '@components/Report/constants';
import { PlayerNotifyFields, toPlayerNotifyFormValues, toPlayerNotifyMetadata } from '@components/Settings/AutomaticActions/PlayerNotifyFields';
import { DashboardReportFields, toDashboardReportFormValues, toDashboardReportMetadata } from '@components/Settings/AutomaticActions/DashboardReportFields';

const { Panel } = Collapse;
const { Text } = Typography;

// These types send only their own metadata keys.
const TYPED_METADATA_BUILDERS: Record<string, (values: any) => IAutomaticAction['metadata']> = {
    [PLAYER_NOTIFY_ACTION_KEY]: toPlayerNotifyMetadata,
    [DASHBOARD_REPORT_ACTION_KEY]: toDashboardReportMetadata,
};


const toDateRangeValue = (
    from: { type: string; value?: string | number },
    to: { type: string; value?: string | number },
    defaults: { startOffset: number; endOffset: number } = { startOffset: -7, endOffset: 0 }
): DateRangeValue => ({
    startDateType: from.type as 'absolute' | 'relative',
    startDateAbsolute: from.type === 'absolute' ? String(from.value ?? '') : '',
    startDateOffset: from.type === 'relative' ? Number(from.value) : defaults.startOffset,
    endDateType: to.type as 'absolute' | 'relative',
    endDateAbsolute: to.type === 'absolute' ? String(to.value ?? '') : '',
    endDateOffset: to.type === 'relative' ? Number(to.value) : defaults.endOffset,
});

// The date UI is driven by the preset's report type, not by which date keys
// happen to exist in the saved config — a preset saved without a date must
// still expose the date control for a date-based report.
const getDateModeForPreset = (preset: any): 'dual' | 'range' | 'single' | 'none' => {
    const requirements = REPORT_TYPES.find(r => r.key === preset?.config?.report_type)?.requirements ?? [];
    if (requirements.includes('dual_date_ranges')) return 'dual';
    if (requirements.includes('date_range')) return 'range';
    if (requirements.includes('date')) return 'single';
    return 'none';
};

const getDateRangeFormValues = (actionData: IAutomaticAction, presets: any[]) => {
    const meta = actionData.metadata;
    const preset = presets.find((p: any) => p.preset_id === meta?.preset_id);
    const mode = getDateModeForPreset(preset);

    if (mode === 'dual') {
        const saved = meta?.dual_date_ranges;
        if (saved && saved.length === 2) {
            return {
                date_range_1: toDateRangeValue(saved[0].from, saved[0].to),
                date_range_2: toDateRangeValue(saved[1].from, saved[1].to),
            };
        }
        const presetRanges = (preset?.config?.dual_date_ranges ?? []) as string[][];
        return {
            date_range_1: toDateRangeValue(
                { type: 'absolute', value: presetRanges[0]?.[0] ?? '' },
                { type: 'absolute', value: presetRanges[0]?.[1] ?? '' }
            ),
            date_range_2: toDateRangeValue(
                { type: 'absolute', value: presetRanges[1]?.[0] ?? '' },
                { type: 'absolute', value: presetRanges[1]?.[1] ?? '' }
            ),
        };
    }

    if (mode === 'single') {
        const savedDate = meta?.date;
        if (savedDate) {
            // legacy absolute dates map to their offset from today; the next save persists them as relative
            return {
                date_relative: savedDate.type === 'relative'
                    ? (savedDate as any).value
                    : dayjs((savedDate as any).value).diff(dayjs().startOf('day'), 'day'),
            };
        }
        return { date_relative: -1 };
    }

    if (mode === 'range') {
        if (meta?.date_range) {
            return { date_range: toDateRangeValue(meta.date_range.from, meta.date_range.to) };
        }
        return {
            date_range: toDateRangeValue(
                { type: 'absolute', value: preset?.config?.date_range?.[0] ?? '' },
                { type: 'absolute', value: preset?.config?.date_range?.[1] ?? '' }
            ),
        };
    }

    return {};
};

// "Today [-1] day(s)": the input holds the signed offset itself
// (0 = the run day, -1 = the day before).
const RelativeDayField = ({ value = -1, onChange, disabled }: {
    value?: number;
    onChange?: (value: number) => void;
    disabled?: boolean;
}) => (
    <Space size={8} align="center">
        <Text>Today</Text>
        <InputNumber
            min={-365}
            max={0}
            value={value}
            onChange={(n) => onChange?.(n ?? -1)}
            style={{ width: 72 }}
            disabled={disabled}
        />
    </Space>
);

// A saved action arrives from the API as an "HH:mm" string, while a newly created
// one carries a Date. dayjs applies a format string to string input only, so
// dayjs(aDate, "HH:mm") returns an Invalid Date — the TimePicker then shows
// "Invalid date" and every cell click stays invalid, leaving "Now" as the only
// way to produce a usable value.
const parseScheduledAt = (scheduledAt: Date | string) =>
    typeof scheduledAt === 'string' ? dayjs(scheduledAt, "HH:mm") : dayjs(scheduledAt);

export const AutomaticActionCard = ({
    automaticActionData,
    // `team.misc` starts out empty, so the action types can still be undefined
    // when saved actions have already been fetched and rendered.
    automaticActionTypes = [],
    key,
    status,
    idx,
    refetchAutomaticActions,
    hideNewAction,
    coachList,
    integrations,
    reportPresets
}: IAutomaticActionCard) => {
    const trainingAPI = new TrainingAPI();
    const navigate = useNavigate();

    const action = automaticActionTypes
        ?.find(e => e.id === automaticActionData?.action_types_id)
    const actionName = action?.name
    const frequency = automaticActionData.frequency.length > 0 ? automaticActionData.frequency[0].split(":")[0] as FREQUENCY_DATA_KEY_TYPE : undefined

    const [activeStatus, setActiveStatus] = useState<boolean>(automaticActionData.is_active)

    const [form] = Form.useForm()
    const actionTypesId = Form.useWatch('action_types_id', form)
    const repeatEvery = Form.useWatch('repeat_every', form) as FREQUENCY_DATA_KEY_TYPE | undefined;
    const selectedValues: string[] = Form.useWatch('frequency', form) || [];
    const selectedPresetName: string | undefined = Form.useWatch('preset_name', form);

    const isPresetReport = automaticActionTypes.find(e => e.id === actionTypesId)?.key === 'preset-report';
    const isPlayerNotify = automaticActionTypes.find(e => e.id === actionTypesId)?.key === PLAYER_NOTIFY_ACTION_KEY;
    const isDashboardReport = automaticActionTypes.find(e => e.id === actionTypesId)?.key === DASHBOARD_REPORT_ACTION_KEY;
    const buildTypedMetadata = TYPED_METADATA_BUILDERS[automaticActionTypes.find(e => e.id === actionTypesId)?.key ?? ''];
    const schedulablePresets = reportPresets.filter((p: any) => ['coach-gps', 'gps', 'wellness', 'flag-one-pager'].includes(p.config.report_type));
    const selectedPreset = reportPresets.find((p: any) => p.name === selectedPresetName);
    const dateMode = getDateModeForPreset(selectedPreset);

    const displayName = automaticActionTypes.find((e) => e.id === actionTypesId)?.name || actionName || '';

    useEffect(() => {
        if (!isPresetReport || !selectedPresetName) return;
        const hasSavedRange = automaticActionData.metadata?.date || automaticActionData.metadata?.date_range || automaticActionData.metadata?.dual_date_ranges;
        if (hasSavedRange) return;
        const preset = reportPresets.find((p: any) => p.name === selectedPresetName);
        if (!preset) return;
        const mode = getDateModeForPreset(preset);
        if (mode === 'dual') {
            const presetRanges = (preset.config?.dual_date_ranges ?? []) as string[][];
            form.setFieldsValue({
                date_range_1: toDateRangeValue(
                    { type: 'absolute', value: presetRanges[0]?.[0] ?? '' },
                    { type: 'absolute', value: presetRanges[0]?.[1] ?? '' }
                ),
                date_range_2: toDateRangeValue(
                    { type: 'absolute', value: presetRanges[1]?.[0] ?? '' },
                    { type: 'absolute', value: presetRanges[1]?.[1] ?? '' }
                ),
            });
        } else if (mode === 'single') {
            form.setFieldsValue({ date_relative: -1 });
        } else if (mode === 'range') {
            form.setFieldsValue({
                date_range: toDateRangeValue(
                    { type: 'absolute', value: preset?.config?.date_range?.[0] ?? '' },
                    { type: 'absolute', value: preset?.config?.date_range?.[1] ?? '' }
                ),
            });
        }
    }, [selectedPresetName]);

    useEffect(() => {
        if (automaticActionData) {
            form.setFieldsValue({
                action_types_id: status === 'new' ? undefined : action?.id,
                repeat_every: frequency || 'weekly',
                frequency: automaticActionData.frequency,
                scheduled_at: parseScheduledAt(automaticActionData.scheduled_at),
                emails: automaticActionData.metadata?.emails || [],
                api_types: automaticActionData.metadata?.api_types || [],
                days_to_pull: automaticActionData.metadata?.days_to_pull || 1,
                preset_name: reportPresets.find((p: any) => p.preset_id === automaticActionData.metadata?.preset_id)?.name,
                ...getDateRangeFormValues(automaticActionData, reportPresets),
                ...toPlayerNotifyFormValues(automaticActionData.metadata),
                ...toDashboardReportFormValues(automaticActionData.metadata)
            })
        }
    }, [automaticActionData, idx, automaticActionTypes])

    return (
        <Collapse
            className="automatic-action-collapse"
            defaultActiveKey={status === 'new' ? [`${key}`] : undefined}
            style={{ backgroundColor: 'white', marginBottom: '10px' }}>
            <Panel
                key={`${key}`}
                header={
                    <Space size={8}>
                        <Typography.Text strong>
                            {displayName || (status === 'new' ? 'New action' : 'Action')}
                        </Typography.Text>
                        {status === 'old' && <Tag>{FREQUENCY_DATA[frequency || 'weekly'].shortLabel}</Tag>}
                    </Space>
                }
                extra={
                    <div className="automatic-action-header-extra" onClick={(e) => { e.stopPropagation() }}>
                        {status === 'old' &&
                            <>
                                <Popconfirm
                                    title="Delete this action?"
                                    okText="Delete"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={async () => {
                                        try {
                                            await trainingAPI.deleteAutomaticActions(automaticActionData.id)
                                            await refetchAutomaticActions()
                                            audioMessage.success('Deleted action')
                                        } catch (err) {
                                            audioMessage.error("Failed to delete")
                                        }
                                    }}
                                >
                                    <Button type="link" danger shape="circle" icon={<DeleteOutlined />} />
                                </Popconfirm>
                                <Switch checked={activeStatus} onChange={async () => {
                                    try {
                                        await trainingAPI.patchAutomaticActions({
                                            id: automaticActionData.id,
                                            is_active: !activeStatus
                                        })
                                        setActiveStatus(!activeStatus)
                                        audioMessage.success('Status updated')
                                    } catch (err) {
                                        audioMessage.error("Failed to update")
                                    }
                                }} size="small" />
                            </>
                        }
                    </div>
                }
            >
                <Form
                    name="automaticAction"
                    form={form}
                    layout="horizontal"
                    labelAlign="left"
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 20 }}
                    validateTrigger="onBlur"
                    initialValues={{
                        action_types_id: action?.id,
                        repeat_every: frequency || 'weekly',
                        frequency: automaticActionData.frequency,
                        scheduled_at: parseScheduledAt(automaticActionData.scheduled_at),
                        emails: automaticActionData.metadata?.emails || [],
                        api_types: automaticActionData.metadata?.api_types || [],
                        days_to_pull: automaticActionData.metadata?.days_to_pull || 1,
                        preset_name: reportPresets.find((p: any) => p.preset_id === automaticActionData.metadata?.preset_id)?.name,
                        ...getDateRangeFormValues(automaticActionData, reportPresets),
                        ...toPlayerNotifyFormValues(automaticActionData.metadata),
                        ...toDashboardReportFormValues(automaticActionData.metadata)
                    }}
                    onFinish={async (values) => {
                        if (status === 'old') {
                            try {
                                await trainingAPI.patchAutomaticActions({
                                    id: automaticActionData.id,
                                    action_types: values.action_types_id,
                                    scheduled_at: values.scheduled_at.format("HH:mm"),
                                    frequency: values.frequency.filter((e: string) => e.includes(values.repeat_every)),
                                    is_active: activeStatus,
                                    metadata: buildTypedMetadata ? buildTypedMetadata(values) : {
                                        emails: automaticActionTypes.find(e => e.id === actionTypesId)?.needs_email ? values.emails : [],
                                        api_types: automaticActionTypes.find(e => e.id === actionTypesId)?.key === 'api-data-pull' ? values.api_types : [],
                                        days_to_pull: automaticActionTypes.find(e => e.id === actionTypesId)?.key === 'api-data-pull' ? values.days_to_pull : 0,
                                        preset_id: automaticActionTypes.find(e => e.id === values.action_types_id)?.key === 'preset-report' ? reportPresets.find((p: any) => p.name === values.preset_name)?.preset_id : undefined,
                                        date: (isPresetReport && dateMode === 'single')
                                            ? { type: 'relative', value: values.date_relative }
                                            : undefined,
                                        date_range: (isPresetReport && dateMode === 'range') ? {
                                            from: values.date_range.startDateType === 'absolute'
                                                ? { type: 'absolute', value: values.date_range.startDateAbsolute }
                                                : { type: 'relative', value: values.date_range.startDateOffset },
                                            to: values.date_range.endDateType === 'absolute'
                                                ? { type: 'absolute', value: values.date_range.endDateAbsolute }
                                                : { type: 'relative', value: values.date_range.endDateOffset },
                                        } : undefined,
                                        dual_date_ranges: (isPresetReport && dateMode === 'dual') ? [
                                            {
                                                from: values.date_range_1.startDateType === 'absolute'
                                                    ? { type: 'absolute', value: values.date_range_1.startDateAbsolute }
                                                    : { type: 'relative', value: values.date_range_1.startDateOffset },
                                                to: values.date_range_1.endDateType === 'absolute'
                                                    ? { type: 'absolute', value: values.date_range_1.endDateAbsolute }
                                                    : { type: 'relative', value: values.date_range_1.endDateOffset },
                                            },
                                            {
                                                from: values.date_range_2.startDateType === 'absolute'
                                                    ? { type: 'absolute', value: values.date_range_2.startDateAbsolute }
                                                    : { type: 'relative', value: values.date_range_2.startDateOffset },
                                                to: values.date_range_2.endDateType === 'absolute'
                                                    ? { type: 'absolute', value: values.date_range_2.endDateAbsolute }
                                                    : { type: 'relative', value: values.date_range_2.endDateOffset },
                                            },
                                        ] : undefined,
                                    }
                                })
                                audioMessage.success(`Updated successfully`);
                            } catch (err: any) {
                                audioMessage.error(err?.response?.data?.error || "Failed to update")
                            }
                        } else {
                            try {
                                await trainingAPI.postAutomaticActions({
                                    action_types: values.action_types_id,
                                    scheduled_at: values.scheduled_at.format("HH:mm"),
                                    frequency: values.frequency.filter((e: string) => e.includes(values.repeat_every)),
                                    is_active: true,
                                    metadata: buildTypedMetadata ? buildTypedMetadata(values) : {
                                        emails: automaticActionTypes.find(e => e.id === actionTypesId)?.needs_email ? values.emails : [],
                                        api_types: automaticActionTypes.find(e => e.id === actionTypesId)?.key === 'api-data-pull' ? values.api_types : [],
                                        preset_id: automaticActionTypes.find(e => e.id === values.action_types_id)?.key === 'preset-report' ? reportPresets.find((p: any) => p.name === values.preset_name)?.preset_id : undefined,
                                        days_to_pull: automaticActionTypes.find(e => e.id === actionTypesId)?.key === 'api-data-pull' ? values.days_to_pull : 0,
                                        date: (isPresetReport && dateMode === 'single')
                                            ? { type: 'relative', value: values.date_relative }
                                            : undefined,
                                        date_range: (isPresetReport && dateMode === 'range') ? {
                                            from: values.date_range.startDateType === 'absolute'
                                                ? { type: 'absolute', value: values.date_range.startDateAbsolute }
                                                : { type: 'relative', value: values.date_range.startDateOffset },
                                            to: values.date_range.endDateType === 'absolute'
                                                ? { type: 'absolute', value: values.date_range.endDateAbsolute }
                                                : { type: 'relative', value: values.date_range.endDateOffset },
                                        } : undefined,
                                        dual_date_ranges: (isPresetReport && dateMode === 'dual') ? [
                                            {
                                                from: values.date_range_1.startDateType === 'absolute'
                                                    ? { type: 'absolute', value: values.date_range_1.startDateAbsolute }
                                                    : { type: 'relative', value: values.date_range_1.startDateOffset },
                                                to: values.date_range_1.endDateType === 'absolute'
                                                    ? { type: 'absolute', value: values.date_range_1.endDateAbsolute }
                                                    : { type: 'relative', value: values.date_range_1.endDateOffset },
                                            },
                                            {
                                                from: values.date_range_2.startDateType === 'absolute'
                                                    ? { type: 'absolute', value: values.date_range_2.startDateAbsolute }
                                                    : { type: 'relative', value: values.date_range_2.startDateOffset },
                                                to: values.date_range_2.endDateType === 'absolute'
                                                    ? { type: 'absolute', value: values.date_range_2.endDateAbsolute }
                                                    : { type: 'relative', value: values.date_range_2.endDateOffset },
                                            },
                                        ] : undefined,
                                    }
                                })
                                hideNewAction()
                                refetchAutomaticActions()
                                audioMessage.success(`Created successfully`);
                            } catch (err: any) {
                                audioMessage.error(err?.response?.data?.error || "Failed to create")
                            }
                        }
                    }}
                    onFinishFailed={() => {
                        audioMessage.warning(`Something went wrong`);
                    }}
                >
                    {/* Action */}
                    <Form.Item
                        label={<Text>Action</Text>}
                        name="action_types_id"
                        rules={[{ required: true, message: 'Please select an action.' }]}
                    >
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={automaticActionTypes.map((e) => {
                                return { value: e.id, label: e.name }
                            })}
                            placeholder="Please select an action to get started"
                            disabled={activeStatus === false}
                        />
                    </Form.Item>

                    {isPlayerNotify && <PlayerNotifyFields disabled={activeStatus === false} />}

                    {isDashboardReport && <DashboardReportFields disabled={activeStatus === false} />}

                    {/* Preset Report */}
                    {isPresetReport && (
                        <Form.Item
                            label={<Text>Preset</Text>}
                            name="preset_name"
                            rules={[{ required: true, message: 'Please select a report preset.' }]}
                            extra={selectedPreset && (
                                <Popover
                                    trigger="hover"
                                    title={selectedPreset.name}
                                    content={<PresetConfigDisplay preset={selectedPreset} />}
                                    overlayStyle={{ maxWidth: 420 }}
                                >
                                    <Typography.Link>See Details</Typography.Link>
                                </Popover>
                            )}
                        >
                            <Select
                                showSearch
                                optionLabelProp="label"
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                placeholder="Please select a report preset"
                                notFoundContent={
                                    <Text type="secondary">
                                        No report presets yet — create one in the Reports tab first.
                                    </Text>
                                }
                                disabled={activeStatus === false}
                            >
                                {schedulablePresets.map((preset: any) => (
                                    <Select.Option key={preset.name} value={preset.name} label={preset.name}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>{preset.name}</span>
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    navigate(`/reports?preset=${encodeURIComponent(preset.name)}`);
                                                }}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }}
                                            />
                                        </div>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {/* Date Range */}
                    {isPresetReport && dateMode === 'single' && (
                        <Form.Item label={<Text>Report for</Text>} name="date_relative" required>
                            <RelativeDayField disabled={activeStatus === false} />
                        </Form.Item>
                    )}
                    {isPresetReport && dateMode === 'range' && (
                        <Form.Item label={<Text>Date Range</Text>} name="date_range" required>
                            <DateRangeField disabled={activeStatus === false} />
                        </Form.Item>
                    )}
                    {isPresetReport && dateMode === 'dual' && (
                        <>
                            <Form.Item label={<Text>Date Range 1</Text>} name="date_range_1" required>
                                <DateRangeField disabled={activeStatus === false} />
                            </Form.Item>
                            <Form.Item label={<Text>Date Range 2</Text>} name="date_range_2" required>
                                <DateRangeField disabled={activeStatus === false} />
                            </Form.Item>
                        </>
                    )}

                    {/* Repeat Every */}
                    <Form.Item
                        label={<Text>Repeat</Text>}
                        name="repeat_every"
                        rules={[{ required: true, message: 'Please select a frequency.' }]}
                    >
                        <Select
                            options={Object.entries(FREQUENCY_DATA).map(([key, value]) => {
                                return { value: key, label: value.label }
                            })}
                            placeholder="Select a frequency"
                            disabled={activeStatus === false}
                        />
                    </Form.Item>

                    {/* Frequency */}
                    <Form.Item
                        label={<Text>Days</Text>}
                        name="frequency"
                        rules={[{ required: true, message: 'Please select at least one day.' }]}
                    >
                        <>
                            {/* Register the field */}
                            <Input type="hidden" />

                            {FREQUENCY_DATA[repeatEvery || 'weekly'].renderType == 'switch' &&
                                <Space wrap>
                                    {FREQUENCY_DATA[repeatEvery || 'weekly'].options.map(opt => {
                                        const checked = selectedValues.includes(opt.value);

                                        return (
                                            <Button
                                                key={opt.value}
                                                type={checked ? 'primary' : 'default'}
                                                shape="round"
                                                style={{ height: 28, minWidth: 28, padding: '1px 8px 0', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                onClick={() => {
                                                    const next = checked
                                                        ? selectedValues.filter((v: string) => v !== opt.value)
                                                        : [...selectedValues, opt.value];
                                                    form.setFieldsValue({ frequency: next });
                                                }}
                                                disabled={activeStatus === false}
                                            >
                                                {opt.label}
                                            </Button>
                                        );
                                    })}
                                </Space>
                            }
                            {FREQUENCY_DATA[repeatEvery || 'weekly'].renderType == 'calendar' &&
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: 244 }}>
                                    {FREQUENCY_DATA[repeatEvery || 'weekly'].options.map(opt => {
                                        // saved values are zero-padded ("monthly:01") — the old calendar UI stored date.format('DD')
                                        const padded = `${FREQUENCY_DATA[repeatEvery || 'weekly'].prefix}:${opt.label.padStart(2, '0')}`;
                                        const checked = selectedValues.includes(padded) || selectedValues.includes(opt.value);

                                        return (
                                            <Button
                                                key={opt.value}
                                                type={checked ? 'primary' : 'default'}
                                                shape="circle"
                                                style={{ width: 28, height: 28, minWidth: 28, padding: '1px 0 0', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                onClick={() => {
                                                    const next = checked
                                                        ? selectedValues.filter((v: string) => v !== padded && v !== opt.value)
                                                        : [...selectedValues, padded];
                                                    form.setFieldsValue({ frequency: next });
                                                }}
                                                disabled={activeStatus === false}
                                            >
                                                {opt.label}
                                            </Button>
                                        );
                                    })}
                                </div>
                            }
                        </>
                    </Form.Item>

                    {/* Scheduled at */}
                    <Form.Item label={<Text>Run at</Text>} required>
                        <Space size={8} align="center">
                            <Form.Item
                                name="scheduled_at"
                                rules={[{ required: true, message: 'Please select a time.' }]}
                                noStyle
                            >
                                <TimePicker
                                    format="HH:mm"
                                    minuteStep={5}
                                    disabled={activeStatus === false}
                                    onSelect={(e) => {
                                        form.setFieldsValue({
                                            scheduled_at: e,
                                        })
                                    }}
                                />
                            </Form.Item>
                            <Text type="secondary">(Time in EST)</Text>
                        </Space>
                    </Form.Item>

                    {/* Emails */}
                    {automaticActionTypes.find(e => e.id === actionTypesId)?.needs_email &&
                        <Form.Item
                            label={<Text>Recipients</Text>}
                            name="emails"
                            rules={[{ required: true, message: 'Please select at least one recipient.' }]}
                        >
                            <Select
                                mode="multiple"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={coachList.map((data) => {
                                    return { value: data.email, label: data.email }
                                })}
                                placeholder="Select recipients"
                                disabled={activeStatus === false}
                            />
                        </Form.Item>
                    }

                    {/* API Types */}
                    {automaticActionTypes.find(e => e.id === actionTypesId)?.key === 'api-data-pull' &&
                        <Form.Item
                            label={<Text>API Type(s)</Text>}
                            name="api_types"
                            rules={[{ required: true, message: 'Please select at least one API type.' }]}
                        >
                            <Select
                                mode="multiple"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={integrations.map((data) => {
                                    return { value: data.key, label: data.name }
                                })}
                                placeholder="Select API type(s)"
                                disabled={activeStatus === false}
                            />
                        </Form.Item>
                    }

                    {/* Days to Pull */}
                    {automaticActionTypes.find(e => e.id === actionTypesId)?.key === 'api-data-pull' &&
                        <Form.Item
                            label={<Text>Day(s) to Pull</Text>}
                            name="days_to_pull"
                            rules={[{ required: true, message: 'Please enter the number of days to pull.' }]}
                        >
                            <InputNumber />
                        </Form.Item>
                    }

                    {/* Submit */}
                    <Form.Item wrapperCol={{ offset: 4, span: 20 }} style={{ textAlign: 'right', marginBottom: 0 }}>
                        <Button type="primary" htmlType="submit" disabled={activeStatus === false}>
                            {status === 'new' ? "Create Action" : "Save"}
                        </Button>
                    </Form.Item>
                </Form>
            </Panel>
        </Collapse >
    )
}

interface IAutomaticActionCard {
    automaticActionData: IAutomaticAction
    automaticActionTypes?: IAutomaticActionType[]
    key: string,
    status: 'new' | 'old',
    idx: number,
    refetchAutomaticActions: () => Promise<void>,
    hideNewAction: () => void,
    coachList: {
        email: string,
        first_name: string,
        last_name: string,
        user_id: number
    }[],
    integrations: IIntegrations[],
    reportPresets: any[]
}

export default AutomaticActionCard